<template>
  <div class="caller-container">
    <div class="avatar-container">
      <img
        v-if="image"
        :src="image"
        width="100%"
        height="100%"
        alt="caller-template" />
    </div>
    <div class="caller-name">
      <div class="name">
        <div
          ref="nameElement"
          :class="{ 'move-name': isOverflowName }">
          {{ name ?? '' }}
        </div>
      </div>
      <div class="name">
        <div
          ref="companyNameElement"
          :class="{ 'move-company': isCompanyOverflow }">
          {{ companyName }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, useTemplateRef, watch } from 'vue';

export interface ICallerArea {
  name?: string;
  companyName?: string;
  image?: string;
}
const props = withDefaults(defineProps<ICallerArea>(), {
  name: '',
  companyName: ''
});
const isOverflowName = ref(false);
const isCompanyOverflow = ref(false);
const animationDurationName = ref('3s');
const moveName = ref('-100%');
const animationDurationCompany = ref('3s');
const moveCompany = ref('-100%');
const nameElementRef = useTemplateRef<HTMLDivElement>('nameElement');
const companyNameElementRef = useTemplateRef<HTMLDivElement>('companyNameElement');
watch([() => props.name, () => props.companyName], () => {
  setTimeout(() => {
    handleOverFlow();
  }, 100);
});
function handleOverFlow() {
  if (nameElementRef.value) {
    const { isOverflow, ratio, duration } = calculateAnimation(nameElementRef.value);
    isOverflowName.value = isOverflow;
    moveName.value = `-${ratio * 100}%`;
    animationDurationName.value = `${duration <= 3 ? 3 : duration}s`;
  }
  if (companyNameElementRef.value) {
    const { isOverflow, ratio, duration } = calculateAnimation(companyNameElementRef.value);
    isCompanyOverflow.value = isOverflow;
    moveCompany.value = `-${ratio * 100}%`;
    animationDurationCompany.value = `${duration <= 3 ? 3 : duration}s`;
  }
}
function calculateAnimation(target: HTMLDivElement) {
  const scrollWith = target.scrollWidth;
  const clientWidth = target.clientWidth;
  const ratio = (scrollWith - clientWidth) / clientWidth;
  const duration = ratio * 3;
  return {
    isOverflow: scrollWith > clientWidth,
    ratio,
    duration
  };
}
onMounted(() => {
  setTimeout(() => {
    handleOverFlow();
  }, 500);
});
</script>

<style scoped lang="scss">
.caller-container {
  display: grid;
  grid-template-columns: max-content auto;
  align-items: center;
  gap: 8px;
  width: 82%;
  container-name: phone;
  container-type: inline-size;
  & .avatar-container {
    background-color: $white;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    height: 60px;
    width: 60px;
    @container phone (max-width: 200px) {
      height: 44px;
      width: 44px;
    }
    & img {
      -webkit-user-drag: none;
      object-fit: contain;
      width: 100%;
      height: 100%;
    }
  }
  & .caller-name {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    color: $white;
    font-size: 0.9rem;
    line-height: 1.4;
    width: 100%;
    overflow: hidden;
    & .name {
      font-size: 1.15rem;
      overflow: hidden;
      width: 100%;
      position: relative;
      & > div {
        width: 100%;
        white-space: nowrap;
        &.move-name {
          font-weight: 500;
          animation: text-marque-name v-bind(animationDurationName) infinite linear;
          animation-delay: 2s;
        }
        &.move-company {
          font-weight: 500;
          animation: text-marque-company v-bind(animationDurationCompany) infinite linear;
          animation-delay: 2s;
        }
      }
      @keyframes text-marque-name {
        0% {
          transform: translateX(0);
        }
        70% {
          transform: translateX(v-bind(moveName));
        }
        100% {
          transform: translateX(v-bind(moveName));
        }
      }
      @keyframes text-marque-company {
        0% {
          transform: translateX(0);
        }
        80% {
          transform: translateX(v-bind(moveCompany));
        }
        100% {
          transform: translateX(v-bind(moveCompany));
        }
      }
    }
    @container phone (max-width: 200px) {
      font-size: 0.8rem;
      line-height: 1.4;
      & .name {
        font-size: 1rem;
      }
    }
  }
}
</style>
